import type { Annotation } from 'api';

import { getLabelGroups, IGNORE_BALE_LABELS, type LABEL_GROUPS, MODEL_FALLBACK_CATEGORIES } from '../../constants';

const DEFAULT_COLOR: readonly number[] = [128, 128, 128];

export interface GroupedAnnotation {
  groupName: string;
  colour: readonly number[];
  originalLabels: readonly string[];
}

export const getUniqueSegmentationCategories = (items: readonly Annotation[]): Map<string, readonly number[]> => {
  return items.reduce((categories, item) => {
    if (!categories.has(item.label) && !IGNORE_BALE_LABELS.includes(item.label)) {
      categories.set(item.label, item.rgb_colour);
    }
    return categories;
  }, new Map<string, readonly number[]>());
};

export const getGroupedAnnotations = (
  annotations: readonly Annotation[],
  model: keyof typeof LABEL_GROUPS,
  showAerosols = false
): readonly GroupedAnnotation[] => {
  const uniqueCategories = getUniqueSegmentationCategories(annotations);
  const modelGroups = getLabelGroups({ showAerosols })[model];

  const { groups, groupedLabels } = createConfiguredGroups(modelGroups, uniqueCategories);

  const ungroupedLabels = findUngroupedLabels(uniqueCategories, groupedLabels);
  const fallbackGroups = createFallbackGroups(ungroupedLabels, model, uniqueCategories);

  return [...groups, ...fallbackGroups];
};

const createConfiguredGroups = (
  modelGroups: Record<string, readonly string[]>,
  uniqueCategories: Map<string, readonly number[]>
): {
  groups: readonly GroupedAnnotation[];
  groupedLabels: ReadonlySet<string>;
} => {
  const groupedLabels = new Set<string>();

  const groups = Object.entries(modelGroups)
    .filter(([_, labelsList]) => labelsList.some((label) => uniqueCategories.has(label)))
    .map(([groupName, labelsList]) => {
      const matchingLabels = labelsList.filter((label) => uniqueCategories.has(label));

      const firstLabel = matchingLabels[0];
      const groupColour = uniqueCategories.get(firstLabel) || DEFAULT_COLOR;

      for (const label of matchingLabels) {
        groupedLabels.add(label);
      }

      return {
        colour: groupColour,
        groupName,
        originalLabels: matchingLabels,
      };
    });

  return {
    groupedLabels: groupedLabels as ReadonlySet<string>,
    groups,
  };
};

const findUngroupedLabels = (
  uniqueCategories: Map<string, readonly number[]>,
  groupedLabels: ReadonlySet<string>
): readonly string[] => {
  return Array.from(uniqueCategories.keys()).filter((label) => !groupedLabels.has(label));
};

const createFallbackGroups = (
  ungroupedLabels: readonly string[],
  model: keyof typeof LABEL_GROUPS,
  uniqueCategories: Map<string, readonly number[]>
): readonly GroupedAnnotation[] => {
  if (ungroupedLabels.length === 0) {
    return [];
  }

  const fallbackCategory = MODEL_FALLBACK_CATEGORIES[model];

  if (fallbackCategory) {
    const firstLabel = ungroupedLabels[0];
    const colour = uniqueCategories.get(firstLabel) || DEFAULT_COLOR;

    return [
      {
        colour,
        groupName: fallbackCategory,
        originalLabels: ungroupedLabels,
      },
    ];
  }

  return ungroupedLabels.map((label) => {
    const colour = uniqueCategories.get(label) || DEFAULT_COLOR;
    return {
      colour,
      groupName: label,
      originalLabels: [label],
    };
  });
};

export const isGroupHidden = (group: GroupedAnnotation, hiddenCategories: readonly string[]): boolean => {
  return group.originalLabels.every((label) => hiddenCategories.includes(label));
};
